/**
 * Marken-Parametrisierung der Konferenz-Oberfläche (18.07.2026).
 *
 * Die App war vollständig Eva-/„Integraler Salon"-gebrandet — für projekt-
 * spezifische Einsätze (IU-Vorlesung mit dem Voice-Agenten „André", brand `iu`)
 * schaltet der URL-Param `?brand=<key>` das sichtbare Chrome um (Tab-Titel,
 * Wortmarke, Consent-Wortlaut, Agent-Kachel-Untertitel/Logo). Default `eva`
 * = byte-identisches Ist-Verhalten, keine Regression.
 *
 * Der ANGEZEIGTE Agenten-NAME kommt wo immer möglich aus dem LiveKit-
 * `participant.name` (der Worker setzt ihn pro Raum via Skript-`display_name`,
 * z.B. „André") — `agentFallbackName` greift nur, solange der Agent noch nicht
 * verbunden ist. Volles Brand-Kit-Theming (Farben/Logos je Marke) bleibt
 * Welle 10c.
 */
export type SalonBrand = {
  key: string;
  /** Wortmarke/Tab-Titel („E-V-A Salon" | „IU Webkonferenz"). */
  appName: string;
  /** Untertitel auf der Agent-Kachel („Integraler Salon" | leer = ausblenden). */
  subtitle: string;
  /** Tagline unter dem Logo (nur wenn showLogo). */
  tagline: string;
  /** E-V-A-3D-Logo auf der Agent-Kachel zeigen? */
  showLogo: boolean;
  /** Fallback-Anzeigename des Agenten, bis participant.name da ist. */
  agentFallbackName: string;
  /** Emoji im Namens-Badge der Agent-Kachel. */
  agentEmoji: string;
  /** Label des Beitreten-Buttons. */
  joinLabel: string;
  /** Consent-Hinweis unter dem PreJoin (DSGVO). */
  consentJoin: string;
  /** Consent-Hinweis im Warteraum. */
  consentWaiting: string;
  /** Wartetext (Gast im Warteraum). */
  waitingLine: string;
  /** Text nach Verbindungsabbruch. */
  disconnectLine: string;
  /** „Salon" | „Konferenz" — für Host-/Regie-Texte. */
  raumWort: string;
  /** Label des Eröffnen-Knopfs im Regie-Panel (das Regie-KOMMANDO bleibt „eröffne den Salon"). */
  openLabel: string;
  /** Orb-Farbwelt der Agent-Kachel (RGB-Tripel als String) + E-V-A-Rangpunkte an/aus. */
  orb: {
    glow: string;
    ring: string;
    presence: string;
    field: string;
    shadow: string;
    core: [string, string, string, string];
    rankDots: boolean;
  };
  /** Landing-Texte (nur relevant, wenn der Launcher-Fallback über die Landing führt). */
  landingLine: string;
  footerLine: string;
  startLabel: string;
  /**
   * Visuelles Theme als CSS-Variablen-Override (18.07.2026): `--brand-*` +
   * `--lk-*` werden per Inline-Style auf dem Seiten-Wurzelelement gesetzt und
   * überschreiben so die eva-Defaults aus styles/globals.css für ALLE Kinder
   * (LiveKit-Chrome, Panels, Bühne, Warteraum). eva = leeres Objekt →
   * byte-/pixel-identisches Ist-Verhalten, null Regressionsrisiko.
   */
  theme: Record<string, string>;
};

const EVA: SalonBrand = {
  key: 'eva',
  appName: 'E-V-A Salon',
  subtitle: 'Integraler Salon',
  tagline: 'Ethik · Verantwortung · Arbeitswelt',
  showLogo: true,
  agentFallbackName: 'Eva',
  agentEmoji: '🌿',
  joinLabel: 'Salon beitreten',
  consentJoin:
    '🔊 Eva, unsere KI-Begleiterin, ist im Salon dabei: sie hört zu und erstellt auf Wunsch ein Gesprächsprotokoll. Mit dem Beitritt stimmst du dem zu.',
  consentWaiting:
    '🔊 Eva, unsere KI-Begleiterin, hört im Salon mit und erstellt auf Wunsch ein Gesprächsprotokoll.',
  waitingLine:
    'Du wirst automatisch in den Salon eingelassen, sobald der Gastgeber bereit ist. Bitte dieses Fenster geöffnet lassen.',
  disconnectLine: 'Die Verbindung zum Salon wurde getrennt.',
  raumWort: 'Salon',
  openLabel: '▶️ Salon eröffnen',
  orb: {
    glow: '38,166,154',
    ring: '120,210,205',
    presence: '122,174,212',
    field: '168,221,224',
    shadow: '47,185,172',
    core: ['#d6f1f2', '#a8dde0', '#26a69a', '#1f6f78'],
    rankDots: true,
  },
  landingLine:
    'Der digitale Resonanzraum der Integralen Salons — in Vollqualität, mit Eva als Begleiterin.',
  footerLine: 'Eine Bühne von E-V-A e.V. · betrieben mit AI-native Consulting',
  startLabel: 'Salon starten',
  theme: {},
};

const IU: SalonBrand = {
  key: 'iu',
  appName: 'IU Webkonferenz',
  subtitle: '',
  tagline: '',
  showLogo: false,
  agentFallbackName: 'André',
  agentEmoji: '🎓',
  joinLabel: 'Konferenz beitreten',
  consentJoin:
    '🔊 André, der KI-Dozent (digitaler Zwilling von Dr. André Salfeld), ist in dieser Konferenz dabei: er hört zu und erstellt auf Wunsch ein Protokoll. Mit dem Beitritt stimmst du dem zu.',
  consentWaiting:
    '🔊 André, der KI-Dozent, hört in der Konferenz mit und erstellt auf Wunsch ein Protokoll.',
  waitingLine:
    'Du wirst automatisch in die Konferenz eingelassen, sobald der Gastgeber bereit ist. Bitte dieses Fenster geöffnet lassen.',
  disconnectLine: 'Die Verbindung zur Konferenz wurde getrennt.',
  raumWort: 'Konferenz',
  openLabel: '▶️ Konferenz eröffnen',
  // IU-CD-Cyanwelt (#4DFAFF/#00E8EC) statt E-V-A-Teal; keine Rangpunkte.
  orb: {
    glow: '0,220,230',
    ring: '77,250,255',
    presence: '77,230,240',
    field: '180,245,250',
    shadow: '0,220,230',
    core: ['#eaffff', '#a9f6fa', '#00e8ec', '#0e7d8a'],
    rankDots: false,
  },
  landingLine: 'Webkonferenz der IU-Lehrveranstaltung — mit André, dem KI-Dozenten.',
  footerLine: 'Betrieben mit AI-native Consulting',
  startLabel: 'Konferenz starten',
  // IU-Look: neutrale dunkle Fläche + IU-Cyan-Akzente, Inter statt Playfair/
  // Cormorant — kein E-V-A-Teal/Nachtblau mehr im Chrome.
  theme: {
    '--brand-accent': '#17b0c2',
    '--brand-accent-2': '#3ed3e0',
    '--brand-accent-3': '#0e8b98',
    '--brand-accent-dim': 'rgba(34, 200, 212, 0.45)',
    '--brand-soft': '#9be8f0',
    '--brand-soft-dim': 'rgba(155, 232, 240, 0.25)',
    '--brand-grad-a': '#4dfaff',
    '--brand-grad-b': '#9be8f0',
    '--brand-persona-a': '#4dfaff',
    '--brand-persona-b': '#9be8f0',
    '--brand-surface': '#18222c',
    '--brand-surface-deep': '#10161d',
    '--brand-surface-raised': '#213040',
    '--brand-text-soft': '#d8f6f8',
    '--brand-state-on': '#4dfaff',
    '--brand-font-display': "'Inter', -apple-system, system-ui, sans-serif",
    '--brand-font-accent': "'Inter', -apple-system, system-ui, sans-serif",
  },
};

/**
 * Generische Marken-Chromes (15.08.2026, Rename eva-salon → meet): die Konferenz-
 * plattform ist marken-neutral; „Eva"/„Salon" sind Persona + Vokabular der Marke
 * E-V-A. Für alle anderen Marken entsteht das Chrome aus Name + Farben
 * (Werte aus marken-assets/brand-kits.json destilliert, statisch wie im
 * whiteboard-Client). Der Agenten-NAME kommt zur Laufzeit aus participant.name
 * (Worker: Vorlage → Voice-Agent → Profil), hier nur ein neutraler Fallback.
 */
function hexRgb(hex: string): string {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(',');
}
function mkBrand(
  key: string,
  appName: string,
  o: { accent: string; accent2: string; ink: string; soft: string; emoji?: string; footer?: string },
): SalonBrand {
  return {
    key,
    appName,
    subtitle: '',
    tagline: '',
    showLogo: false,
    agentFallbackName: 'Assistenz',
    agentEmoji: o.emoji ?? '🤖',
    joinLabel: 'Konferenz beitreten',
    consentJoin:
      '🔊 Eine KI-Assistenz ist in dieser Konferenz dabei: sie hört zu und erstellt auf Wunsch ein Protokoll. Mit dem Beitritt stimmst du dem zu.',
    consentWaiting: '🔊 Eine KI-Assistenz hört in der Konferenz mit und erstellt auf Wunsch ein Protokoll.',
    waitingLine:
      'Du wirst automatisch in die Konferenz eingelassen, sobald der Gastgeber bereit ist. Bitte dieses Fenster geöffnet lassen.',
    disconnectLine: 'Die Verbindung zur Konferenz wurde getrennt.',
    raumWort: 'Konferenz',
    openLabel: '▶️ Konferenz eröffnen',
    orb: {
      glow: hexRgb(o.accent),
      ring: hexRgb(o.accent2),
      presence: hexRgb(o.accent2),
      field: hexRgb(o.soft),
      shadow: hexRgb(o.accent),
      core: ['#f4f8fb', o.soft, o.accent, o.ink],
      rankDots: false,
    },
    landingLine: `${appName} — Webkonferenz in Vollqualität, mit KI-Assistenz auf Wunsch.`,
    footerLine: o.footer ?? 'Betrieben mit AI-native Consulting',
    startLabel: 'Konferenz starten',
    theme: {
      '--brand-accent': o.accent,
      '--brand-accent-2': o.accent2,
      '--brand-accent-3': o.ink,
      '--brand-accent-dim': `rgba(${hexRgb(o.accent)}, 0.45)`,
      '--brand-soft': o.soft,
      '--brand-soft-dim': `rgba(${hexRgb(o.soft)}, 0.25)`,
      '--brand-grad-a': o.accent2,
      '--brand-grad-b': o.soft,
      '--brand-persona-a': o.accent2,
      '--brand-persona-b': o.soft,
      '--brand-surface': '#18222c',
      '--brand-surface-deep': '#10161d',
      '--brand-surface-raised': '#213040',
      '--brand-text-soft': '#e6eef5',
      '--brand-state-on': o.accent2,
      '--brand-font-display': "'Inter', -apple-system, system-ui, sans-serif",
      '--brand-font-accent': "'Inter', -apple-system, system-ui, sans-serif",
    },
  };
}

/** Neutraler Plattform-Default (kein Marken-Param, keine Server-Marke). */
const MEET = mkBrand('meet', 'Meet', { accent: '#1E66E0', accent2: '#5AA9FF', ink: '#0C2A4D', soft: '#BFDBFE' });
const ANC = mkBrand('anc', 'ANC Meet', { accent: '#1E66E0', accent2: '#5AA9FF', ink: '#0C2A4D', soft: '#BFDBFE', footer: 'AI-native Consulting' });
// CD 3.0 (20.08.2026): Aubergine/Gold von zahnrat-coaching.de + Prezi-Seminar (vorher Marineblau/Tuerkis v1)
const ZAHNRAT = mkBrand('zahnrat', 'ZahnRat Meet', { accent: '#C7A057', accent2: '#8A4E91', ink: '#3A1440', soft: '#EAD7BE', emoji: '🦷' });
const IFIM = mkBrand('ifim', 'IfiM Meet', { accent: '#26A69A', accent2: '#5CC7BE', ink: '#1F2E3D', soft: '#A8DDE0' });
const HEIMATHAFEN = mkBrand('heimathafen', 'Heimathafen Meet', { accent: '#E1E000', accent2: '#F0EF6A', ink: '#003A3F', soft: '#F6F5B0', emoji: '⚓' });

const BRANDS: Record<string, SalonBrand> = {
  meet: MEET, eva: EVA, iu: IU, anc: ANC, zahnrat: ZAHNRAT, ifim: IFIM, heimathafen: HEIMATHAFEN,
};

/**
 * Inline-Style-Objekt mit den CSS-Variablen-Overrides der Marke — auf das
 * Seiten-Wurzelelement legen (Server-Render → kein Flash). eva = {}.
 */
export function brandStyleVars(brand: SalonBrand): Record<string, string> {
  return brand.theme || {};
}

/** Pure Auflösung (server- und client-tauglich, hydration-sicher via Prop). */
export function brandFromKey(key?: string | null): SalonBrand {
  return BRANDS[(key || 'meet').toLowerCase()] ?? MEET;
}

/**
 * Client-only Marken-Anker (18.07.2026): das Raum-Root (PageClientImpl) setzt
 * hier die SERVER-seitig aufgelöste Marke (agent-pm-Lookup) — damit liefert
 * getBrand() auch für Links OHNE ?brand=-Param die richtige Marke. Bewusst
 * nur im Browser (Server-Modulzustand würde zwischen Requests leaken).
 */
let activeBrandKey: string | undefined;

export function setActiveBrandKey(key?: string | null): void {
  if (typeof window !== 'undefined') activeBrandKey = key || undefined;
}

/**
 * Client-seitige Auflösung: ?brand=-Param → server-gesetzter Anker → eva.
 * NUR in Komponenten nutzen, die erst nach Client-State rendern
 * (Konferenz/Warteraum) — sonst Prop-Weg über brandFromKey (Hydration).
 */
export function getBrand(): SalonBrand {
  if (typeof window === 'undefined') return EVA;
  try {
    const p = new URLSearchParams(window.location.search).get('brand');
    return brandFromKey(p || activeBrandKey);
  } catch {
    return brandFromKey(activeBrandKey);
  }
}

/** Hängt den aktuellen ?brand=-Param an einen App-Link (für teilbare Links). */
export function withBrandParam(url: string): string {
  if (typeof window === 'undefined') return url;
  try {
    const key = new URLSearchParams(window.location.search).get('brand');
    if (!key || brandFromKey(key).key === 'meet') return url;
    return url + (url.includes('?') ? '&' : '?') + 'brand=' + encodeURIComponent(key);
  } catch {
    return url;
  }
}
